// ============================================================
// ACCOMMODATION FORM - STATIC CONFIG
// ------------------------------------------------------------
// Pulled out of AccommodationForm.jsx (which had grown past
// 1700 lines) so the component file only has to hold wiring
// and rendering. Nothing in here depends on component state -
// it's just the wizard's shape: the empty form, its steps, who
// can edit what, and a couple of small media-cleanup helpers
// used by the draft/autosave logic.
// ============================================================

/* ============================================================
   INITIAL FORM DATA
============================================================ */

export const initialFormData = {
    // -----------------------------------------------------
    // BASIC
    // -----------------------------------------------------

    title: '',
    slug: '',
    overview: '',
    description: '',

    propertyType: '',
    propertyCategory: '',

    maxGuests: 0,
    entirePropertyAvailable: false,

    totalBedrooms: 0,
    totalBathrooms: 0,
    totalUnits: 0,

    highlights: [],
    languages: [],

    // -----------------------------------------------------
    // LOCATION
    // -----------------------------------------------------

    address: {
        line1: '',
        line2: '',
        landmark: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',

        geo: {
            type: 'Point',
            coordinates: [0, 0]
        }
    },

    // -----------------------------------------------------
    // HOST
    // -----------------------------------------------------

    host: {
        organizerId: ''
    },

    // -----------------------------------------------------
    // UNITS
    // -----------------------------------------------------

    units: [],

    // -----------------------------------------------------
    // AMENITIES
    // -----------------------------------------------------

    amenities: {
        essential: [],
        safety: [],
        kitchen: [],
        outdoor: [],
        entertainment: [],
        other: []
    },

    // -----------------------------------------------------
    // SERVICES
    // -----------------------------------------------------

    services: [],

    // -----------------------------------------------------
    // POLICIES
    // -----------------------------------------------------

    policies: {
        checkIn: '',
        checkOut: '',

        earlyCheckIn: false,
        lateCheckOut: false,

        minimumStay: 1,
        maximumStay: null,

        advanceBookingDays: 365,

        sameDayBooking: false,
        instantBooking: false,

        cancellation: {
            enabled: true,
            rules: []
        },

        houseRules: []
    },

    // -----------------------------------------------------
    // MEDIA
    // -----------------------------------------------------

    /*
     * Keep this as an object because the autosave/media
     * structure uses media.images.
     */
    media: {
        images: [],
        videos: []
    },

    cardImages: [],
    videos: [],

    // -----------------------------------------------------
    // BOOKING SETTINGS
    // -----------------------------------------------------

    bookingSettings: {
        instantBooking: false,
        requestToBook: true,

        minimumStay: 1,
        maximumStay: null,

        advanceBookingDays: 365,
        sameDayBooking: false
    },

    // -----------------------------------------------------
    // PAYMENT
    // -----------------------------------------------------

    paymentOptions: {
        onlinePayment: true,
        payAtProperty: false,
        partialPayment: false
    },

    // -----------------------------------------------------
    // ADDITIONAL
    // -----------------------------------------------------

    inclusions: [],
    exclusions: [],
    thingsToCarry: [],
    safetyMeasures: [],
    terms: [],

    faqs: [],

    nearbyPlaces: [],

    accessibility: [],

    // -----------------------------------------------------
    // SEO
    // -----------------------------------------------------

    seo: {
        metaTitle: '',
        metaDescription: '',
        keywords: []
    },

    // -----------------------------------------------------
    // SEASONAL
    // -----------------------------------------------------

    seasonalAvailability: []
};

/* ============================================================
   STEPS
============================================================ */

export const steps = [
    {
        id: 'basic',
        label: 'Basic Information',
        description: 'Property details'
    },

    {
        id: 'location',
        label: 'Location',
        description: 'Address & map'
    },

    {
        id: 'units',
        label: 'Accommodation Units',
        description: 'Rooms, tents, villas'
    },

    {
        id: 'amenities',
        label: 'Amenities',
        description: 'Facilities & features'
    },

    {
        id: 'pricing',
        label: 'Pricing & Availability',
        description: 'Rates & inventory'
    },

    {
        id: 'services',
        label: 'Services',
        description: 'Food, transport & other services'
    },

    {
        id: 'policies',
        label: 'Policies',
        description: 'Cancellation & house rules'
    },

    {
        id: 'media',
        label: 'Media',
        description: 'Photos & videos'
    },

    {
        id: 'additional',
        label: 'Additional Information',
        description: 'FAQs, inclusions, SEO'
    },

    {
        id: 'review',
        label: 'Review & Submit',
        description: 'Check & submit'
    }
];

/* ============================================================
   PERMISSIONS
============================================================ */

export const permissions = {
    organizer: {
        basic: true,
        location: true,
        units: true,
        amenities: true,
        pricing: true,
        services: true,
        policies: true,
        media: true,
        additional: true,
        review: true,

        seo: false,
        verification: false,
        status: false,
        organizer: false
    },

    support: {
        basic: true,
        location: true,
        units: true,
        amenities: true,
        pricing: true,
        services: true,
        policies: true,
        media: true,
        additional: true,
        review: true,

        seo: true,
        verification: true,
        status: true,
        organizer: true
    },

    admin: {
        basic: true,
        location: true,
        units: true,
        amenities: true,
        pricing: true,
        services: true,
        policies: true,
        media: true,
        additional: true,
        review: true,

        seo: true,
        verification: true,
        status: true,
        organizer: true
    }
};

/* ============================================================
   MEDIA / DRAFT HELPERS
============================================================ */

export const isObject = (value) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

export const toNumber = (value, fallback = 0) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
};

export const cleanFileObject = (item) => {
    if (!item) {
        return null;
    }

    if (typeof item === 'string') {
        return item;
    }

    if (item.file) {
        return null;
    }

    return item.url || item.src || item.path || null;
};

export const normalizeMedia = (media) => {
    if (Array.isArray(media)) {
        return {
            images: media.map(cleanFileObject).filter(Boolean),

            videos: []
        };
    }

    if (!isObject(media)) {
        return {
            images: [],
            videos: []
        };
    }

    return {
        ...media,

        images: Array.isArray(media.images) ? media.images.map(cleanFileObject).filter(Boolean) : [],

        videos: Array.isArray(media.videos) ? media.videos.map(cleanFileObject).filter(Boolean) : []
    };
};
